import React, { useEffect, useState, useCallback } from "react"
import { toast } from "react-hot-toast"
import axiosWrapper from "../../utils/AxiosWrapper"
import Heading from "../../components/Heading"
import CustomButton from "../../components/CustomButton"
import Loading from "../../components/Loading"

const STATUS_OPTIONS = [
  "new",
  "contacted",
  "in_progress",
  "converted",
  "lost",
  "spam",
]

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("userToken")}`,
})

const formatAdmin = (a) =>
  a ? `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email : "—"

const Leads = () => {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [committedQ, setCommittedQ] = useState("")
  const [loading, setLoading] = useState(true)
  const [admins, setAdmins] = useState([])
  const [modalLead, setModalLead] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [followNote, setFollowNote] = useState("")
  const [followNextAt, setFollowNextAt] = useState("")

  const loadLeads = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      if (statusFilter) params.set("status", statusFilter)
      if (committedQ.trim()) params.set("q", committedQ.trim())
      const res = await axiosWrapper.get(`/leads?${params.toString()}`, {
        headers: authHeaders(),
      })
      if (res.data.success) {
        setItems(res.data.data.items || [])
        setTotal(res.data.data.total || 0)
      } else toast.error(res.data.message)
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load leads")
    } finally {
      setLoading(false)
    }
  }, [page, limit, statusFilter, committedQ])

  const loadAdmins = async () => {
    try {
      const res = await axiosWrapper.get("/admin", { headers: authHeaders() })
      if (res.data.success) setAdmins(res.data.data || [])
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    loadAdmins()
  }, [])

  useEffect(() => {
    loadLeads()
  }, [loadLeads])

  const openModal = async (id) => {
    try {
      const res = await axiosWrapper.get(`/leads/${id}`, { headers: authHeaders() })
      if (!res.data.success) {
        toast.error(res.data.message)
        return
      }
      const L = res.data.data
      setModalLead(L)
      setEditForm({
        name: L.name,
        phone: L.phone,
        email: L.email || "",
        programInterest: L.programInterest,
        message: L.message || "",
        status: L.status,
        assignedTo: L.assignedTo?._id || "",
        adminNotes: L.adminNotes || "",
      })
      setFollowNote("")
      setFollowNextAt("")
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to open lead")
    }
  }

  const saveLead = async () => {
    if (!modalLead || !editForm) return
    try {
      toast.loading("Saving…")
      const body = {
        ...editForm,
        assignedTo: editForm.assignedTo || null,
      }
      const res = await axiosWrapper.patch(`/leads/${modalLead._id}`, body, {
        headers: authHeaders(),
      })
      toast.dismiss()
      if (res.data.success) {
        toast.success(res.data.message)
        setModalLead(res.data.data)
        loadLeads()
      } else toast.error(res.data.message)
    } catch (e) {
      toast.dismiss()
      toast.error(e.response?.data?.message || "Save failed")
    }
  }

  const addFollowUp = async () => {
    if (!modalLead || !followNote.trim()) {
      toast.error("Enter a follow-up note")
      return
    }
    try {
      toast.loading("Adding follow-up…")
      const res = await axiosWrapper.post(
        `/leads/${modalLead._id}/follow-ups`,
        { note: followNote.trim(), nextFollowUpAt: followNextAt || undefined },
        { headers: authHeaders() }
      )
      toast.dismiss()
      if (res.data.success) {
        toast.success(res.data.message)
        setModalLead(res.data.data)
        setFollowNote("")
        setFollowNextAt("")
        loadLeads()
      } else toast.error(res.data.message)
    } catch (e) {
      toast.dismiss()
      toast.error(e.response?.data?.message || "Failed to add follow-up")
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div className="px-4 pb-12">
      <Heading text="Lead management" />

      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
            Status
          </label>
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setPage(1)
              setStatusFilter(e.target.value)
            }}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
            Search
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Name, phone, email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1)
                setCommittedQ(searchInput.trim())
              }
            }}
          />
        </div>
        <CustomButton
          type="button"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
          onClick={() => {
            setPage(1)
            setCommittedQ(searchInput.trim())
          }}
        >
          Apply
        </CustomButton>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto border rounded-xl bg-white shadow-sm">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-bold">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Program</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3"> </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No leads yet. Submissions from the website contact form appear here.
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row._id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{row.name}</td>
                    <td className="px-4 py-3">{row.phone}</td>
                    <td className="px-4 py-3">{row.programInterest}</td>
                    <td className="px-4 py-3 capitalize">{row.status.replace("_", " ")}</td>
                    <td className="px-4 py-3">{formatAdmin(row.assignedTo)}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {row.updatedAt
                        ? new Date(row.updatedAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-blue-600 font-semibold hover:underline"
                        onClick={() => openModal(row._id)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
        <span>
          Page {page} of {totalPages} ({total} leads)
        </span>
        <div className="flex gap-2">
          <CustomButton
            type="button"
            className="px-3 py-1 border rounded-lg disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </CustomButton>
          <CustomButton
            type="button"
            className="px-3 py-1 border rounded-lg disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </CustomButton>
        </div>
      </div>

      {modalLead && editForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">Lead: {modalLead.name}</h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                onClick={() => {
                  setModalLead(null)
                  setEditForm(null)
                }}
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Phone</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Email</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Program</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.programInterest}
                  onChange={(e) =>
                    setEditForm({ ...editForm, programInterest: e.target.value })
                  }
                >
                  <option value="B.Ed">B.Ed</option>
                  <option value="B.P.Ed">B.P.Ed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">
                  Assign to
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={editForm.assignedTo}
                  onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {admins.map((a) => (
                    <option key={a._id} value={a._id}>
                      {formatAdmin(a)} ({a.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold text-gray-500 uppercase">Their message</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 mt-1 min-h-[70px]"
                value={editForm.message}
                onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
              />
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold text-gray-500 uppercase">Internal notes</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 mt-1 min-h-[60px]"
                value={editForm.adminNotes}
                onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })}
              />
            </div>

            <CustomButton
              type="button"
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg mb-6"
              onClick={saveLead}
            >
              Save changes
            </CustomButton>

            <h4 className="font-bold text-gray-800 mb-2 border-t pt-4">Follow-ups</h4>
            <ul className="space-y-2 mb-4 max-h-40 overflow-y-auto text-sm">
              {(modalLead.followUps || []).length === 0 ? (
                <li className="text-gray-500">No follow-ups yet.</li>
              ) : (
                [...modalLead.followUps].reverse().map((f) => (
                  <li key={f._id} className="border rounded-lg p-2 bg-gray-50">
                    <div className="text-gray-800">{f.note}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {f.createdBy
                        ? `${f.createdBy.firstName || ""} ${f.createdBy.lastName || ""}`.trim()
                        : "Admin"}{" "}
                      · {f.createdAt ? new Date(f.createdAt).toLocaleString() : ""}
                      {f.nextFollowUpAt && (
                        <span>
                          {" "}
                          · Next: {new Date(f.nextFollowUpAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>

            <div className="space-y-2">
              <textarea
                className="w-full border rounded-lg px-3 py-2"
                placeholder="New follow-up note…"
                value={followNote}
                onChange={(e) => setFollowNote(e.target.value)}
              />
              <div>
                <label className="text-xs text-gray-500">Next follow-up (optional)</label>
                <input
                  type="datetime-local"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={followNextAt}
                  onChange={(e) => setFollowNextAt(e.target.value)}
                />
              </div>
              <CustomButton
                type="button"
                className="bg-green-600 text-white px-4 py-2 rounded-lg"
                onClick={addFollowUp}
              >
                Add follow-up
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Leads
